import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/api/client';
/**
 * Pinia issues store — holds the entity map and ordered list IDs for the
 * current filter view. Both IssuesListView and IssueDetailView read from here.
 *
 * IssueDetailDto is a structural superset of IssueListItemDto, so the map can
 * hold either shape. When detail view opens a cold deep-link, it stores the
 * full DetailDto; the list view stores ListItemDto shapes.
 */
export const useIssuesStore = defineStore('issues', () => {
    const entitiesById = ref(new Map());
    const listIds = ref([]);
    const nextCursor = ref(null);
    const totalCount = ref(0);
    const openCount = ref(0);
    const criticalCount = ref(0);
    /**
     * Called by useIssuesSignalR when a SignalR `issueChanged` event arrives.
     * Re-fetches the single item from GET /v1/issues/{id} and patches it in
     * place within the entity map. Does NOT reload the full list.
     */
    async function handleIssueChanged(issueId, _changeKind) {
        // Only patch if the issue is already in our list (avoids random detail fetches).
        if (!entitiesById.value.has(issueId))
            return;
        try {
            const { data } = await api.GET('/v1/issues/{id}', {
                params: { path: { id: issueId } },
            });
            if (data) {
                entitiesById.value.set(issueId, data);
            }
        }
        catch {
            // Non-fatal — stale row will be correct on next list refresh.
        }
    }
    /**
     * Returns the IDs immediately before and after `currentId` in the ordered
     * list, for the Prev/Next navigation in IssueDetailView.
     */
    function adjacentIds(currentId) {
        const idx = listIds.value.indexOf(currentId);
        return {
            prevId: idx > 0 ? (listIds.value[idx - 1] ?? null) : null,
            nextId: idx < listIds.value.length - 1 ? (listIds.value[idx + 1] ?? null) : null,
        };
    }
    /** Upsert an item (called by composables after writes). */
    function upsert(item) {
        entitiesById.value.set(item.id, item);
    }
    /** Prepend a new issue to the list (called after POST /v1/issues). */
    function prepend(item) {
        entitiesById.value.set(item.id, item);
        listIds.value.unshift(item.id);
        totalCount.value += 1;
    }
    /** Replace the current list page (called by useIssues on fresh load). */
    function setPage(items, next, total, open, critical) {
        listIds.value = items.map((i) => i.id);
        for (const item of items)
            entitiesById.value.set(item.id, item);
        nextCursor.value = next;
        totalCount.value = total;
        openCount.value = open;
        criticalCount.value = critical;
    }
    /** Append the next page (loadMore). */
    function appendPage(items, next, total, open, critical) {
        for (const item of items) {
            entitiesById.value.set(item.id, item);
            if (!listIds.value.includes(item.id)) {
                listIds.value.push(item.id);
            }
        }
        nextCursor.value = next;
        totalCount.value = total;
        openCount.value = open;
        criticalCount.value = critical;
    }
    return {
        entitiesById,
        listIds,
        nextCursor,
        totalCount,
        openCount,
        criticalCount,
        handleIssueChanged,
        adjacentIds,
        upsert,
        prepend,
        setPage,
        appendPage,
    };
});
